import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { UserRole } from '@sphere/auth/domain';
import type { DocsArticle, RoleDocsMeta } from './docs.types';

const {
  TEACHER,
  STUDENT,
  PRINCIPAL,
  ADMIN,
  PEDAGOGICAL_COORDINATOR,
  ADMINISTRATIVE_MANAGER,
  SUPPORT,
} = UserRole;

const LEADERSHIP = [PRINCIPAL, ADMIN] as const;

function ArticleBody({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export const ROLE_DOCS_META: Record<UserRole, RoleDocsMeta> = {
  [TEACHER]: {
    title: 'Guide enseignant',
    intro:
      'Sphere vous aide à organiser vos cours, faire travailler les élèves et suivre leurs résultats. Chaque guide explique à quoi sert un module et ce que vous y faites concrètement.',
  },
  [STUDENT]: {
    title: 'Guide élève',
    intro:
      'Votre espace sert à apprendre : consulter les cours, s’entraîner, rendre des évaluations et voir vos progrès. Voici ce que chaque module vous permet de faire.',
  },
  [PRINCIPAL]: {
    title: 'Guide direction',
    intro:
      'Vous pilotez l’établissement : qui y travaille, comment les classes sont organisées, ce qui se communique, et comment les élèves progressent. Les guides détaillent chaque module de votre menu.',
  },
  [ADMIN]: {
    title: 'Guide administrateur',
    intro:
      'Vous administrez l’établissement et supervisez le pédagogique. Chaque guide précise le rôle d’un module et les actions typiques.',
  },
  [PEDAGOGICAL_COORDINATOR]: {
    title: 'Guide coordinateur pédagogique',
    intro:
      'Vous assurez la cohérence des enseignements et accompagnez les enseignants. Ces guides décrivent vos modules et ce que vous y faites.',
  },
  [ADMINISTRATIVE_MANAGER]: {
    title: 'Guide gestionnaire administratif',
    intro:
      'Votre périmètre est administratif : établissement, membres, classes. Pas de création de cours ni d’évaluations — uniquement l’organisation des personnes et des groupes.',
  },
  [SUPPORT]: {
    title: 'Guide support',
    intro:
      'Vous diagnostiquez en lecture seule et orientez les utilisateurs. Ces guides expliquent ce que chaque profil voit, pour les renvoyer au bon module.',
  },
};

export const DOCS_CATALOG: DocsArticle[] = [
  // ─── Teacher ───────────────────────────────────────────────
  {
    slug: 'teacher-getting-started',
    title: 'Premiers pas enseignant',
    summary:
      'À quoi sert Sphere pour un enseignant, et par où commencer le premier jour.',
    roles: [TEACHER],
    section: 'start',
    tags: ['démarrage', 'vue d’ensemble', 'modules'],
    body: (
      <ArticleBody>
        <h2>Ce que Sphere vous permet de faire</h2>
        <p>
          En tant qu’enseignant, vous construisez le parcours de vos classes :
          préparer le contenu du cours, proposer des exercices et évaluations,
          communiquer, puis suivre qui progresse (ou décroche). L’IA accélère la
          création et la correction, mais vous gardez la main sur ce qui est
          publié.
        </p>
        <h2>Vos modules (menu de gauche)</h2>
        <ul>
          <li>
            <strong>Mes Classes</strong> — vos effectifs : qui est dans quelle
            classe.
          </li>
          <li>
            <strong>Mes Cours (enseignements)</strong> — le « contrat »
            pédagogique : une classe + une matière + un niveau qui vous est
            attribué.
          </li>
          <li>
            <strong>Contenus</strong> — chapitres, leçons et ressources (le
            matériel pédagogique).
          </li>
          <li>
            <strong>Exercices &amp; Évaluations</strong> — ce que les élèves
            font pour s’entraîner ou être notés.
          </li>
          <li>
            <strong>Suivi</strong> — résultats et activité (vous / établissement).
          </li>
          <li>
            <strong>Communication</strong> — annonces et emails.
          </li>
          <li>
            <strong>IA Studio</strong> — générer quiz, synthèses, exercices ou
            corriger une copie.
          </li>
        </ul>
        <h2>Par où commencer</h2>
        <ol>
          <li>
            Vérifiez l’établissement actif dans la barre du haut (contexte de
            travail).
          </li>
          <li>
            Ouvrez <Link to="/classes">Mes Classes</Link> pour confirmer vos
            groupes.
          </li>
          <li>
            Allez dans <Link to="/teachings">Mes Cours</Link> : c’est le point
            d’entrée pour structurer un enseignement.
          </li>
          <li>
            Ensuite créez chapitres → leçons → ressources, puis une activité à
            assigner.
          </li>
        </ol>
        <p>
          Détails :{' '}
          <Link to="/docs/teacher-content-workflow">structurer un cours</Link>,{' '}
          <Link to="/docs/teacher-activities">activités</Link>,{' '}
          <Link to="/docs/teacher-ai-studio">IA Studio</Link>.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'teacher-content-workflow',
    title: 'Structurer un cours',
    summary:
      'Enseignement, chapitre, leçon, ressource : à quoi sert chaque niveau et comment les enchaîner.',
    roles: [TEACHER],
    section: 'workflows',
    tags: ['chapitre', 'leçon', 'ressource', 'contenu', 'enseignement'],
    body: (
      <ArticleBody>
        <h2>Pourquoi cette structure</h2>
        <p>
          Sphere sépare l’organisation du cours (qui enseigne quoi à qui) du
          contenu pédagogique (ce que l’élève lit ou regarde). Ainsi vous
          réutilisez des leçons, rattachez des PDF, et les élèves retrouvent tout
          au même endroit.
        </p>
        <h2>Les 4 niveaux</h2>
        <ul>
          <li>
            <strong>Enseignement</strong> (
            <Link to="/teachings">Mes Cours</Link>) — cadre : classe + matière +
            niveau. C’est votre « cours de Maths 6e A », pas encore le contenu.
          </li>
          <li>
            <strong>Chapitre</strong> (<Link to="/chapters">Chapitres</Link>) —
            une séquence thématique (ex. « Les fractions »). Sert à découper
            l’année ou le trimestre.
          </li>
          <li>
            <strong>Leçon</strong> (<Link to="/lessons">Leçons</Link>) — une
            séance ou un objectif précis dans le chapitre. C’est l’unité que
            l’élève ouvre pour apprendre.
          </li>
          <li>
            <strong>Ressource</strong> (
            <Link to="/resources">Ressources</Link>) — le support : PDF de cours,
            vidéo, lien externe, image. Sans ressource, la leçon reste une coquille
            vide.
          </li>
        </ul>
        <h2>Ce que vous faites concrètement</h2>
        <ol>
          <li>
            Ouvrez l’enseignement dans <Link to="/teachings">Mes Cours</Link>.
          </li>
          <li>
            Créez un chapitre (titre, ordre, éventuellement description).
          </li>
          <li>Ajoutez une ou plusieurs leçons dans ce chapitre.</li>
          <li>
            Sur chaque leçon, uploadez ou liez des ressources (PDF, lien…).
          </li>
        </ol>
        <p>
          Les <em>activités</em> (quiz, évaluations) ne se créent pas ici : elles
          vivent dans Exercices &amp; Évaluations, mais peuvent être liées à une
          leçon pour que l’élève sache sur quoi s’entraîner.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'teacher-activities',
    title: 'Exercices et évaluations',
    summary:
      'Créer, assigner et suivre ce que les élèves font pour s’entraîner ou être évalués.',
    roles: [TEACHER],
    section: 'workflows',
    tags: ['activité', 'évaluation', 'assignation', 'tentative'],
    body: (
      <ArticleBody>
        <h2>À quoi sert ce module</h2>
        <p>
          <Link to="/learning/exercises">Exercices &amp; Évaluations</Link> est
          l’espace où vous transformez le cours en travail élève : questions,
          quiz, textes à trous, devoirs notés. Ce n’est plus le contenu à lire,
          c’est ce qu’ils doivent <em>faire</em>.
        </p>
        <h2>Concepts utiles</h2>
        <ul>
          <li>
            <strong>Activité</strong> — le sujet (énoncé, type, barème éventuel).
          </li>
          <li>
            <strong>Assignation</strong> — à qui elle est proposée (classe
            entière ou élèves ciblés) et sur quelle période.
          </li>
          <li>
            <strong>Tentative</strong> — le passage d’un élève (en cours ou
            terminé, avec score).
          </li>
        </ul>
        <h2>Ce que vous y faites</h2>
        <ol>
          <li>Créer une activité (manuellement ou via IA Studio).</li>
          <li>
            La rattacher éventuellement à une leçon pour le contexte pédagogique.
          </li>
          <li>L’assigner à une classe ou à une sélection d’élèves.</li>
          <li>
            Suivre les tentatives, relancer, et corriger (dont correction de
            copie via <Link to="/ai/grade-copy">IA Studio</Link>).
          </li>
        </ol>
        <p>
          Les résultats remontent aussi dans{' '}
          <Link to="/docs/teacher-analytics">le suivi / analytics</Link>.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'teacher-ai-studio',
    title: 'IA Studio pour enseigner',
    summary:
      'Chaque outil IA : à quoi il sert, quand l’utiliser, et ce que vous en faites ensuite.',
    roles: [TEACHER],
    section: 'modules',
    tags: ['ia', 'quiz', 'correction', 'synthèse', 'exercices'],
    body: (
      <ArticleBody>
        <h2>Rôle du module</h2>
        <p>
          <Link to="/ai">IA Studio</Link> accélère la production pédagogique. Ce
          n’est pas un cours autonome : vous générez un brouillon, vous le
          relisez, puis vous le publiez dans Contenus ou Activités.
        </p>
        <h2>Les outils et ce qu’on y fait</h2>
        <ul>
          <li>
            <Link to="/ai/quiz">Quiz</Link> — générer un QCM à partir d’un thème
            ou d’un texte, pour un entraînement ou une évaluation courte.
          </li>
          <li>
            <Link to="/ai/cloze-quiz">Texte à trous</Link> — créer un exercice de
            mémorisation / compréhension à partir d’un passage.
          </li>
          <li>
            <Link to="/ai/summary">Synthèse</Link> — résumer un cours ou un PDF
            pour une fiche élève ou une intro de leçon.
          </li>
          <li>
            <Link to="/ai/exercises">Exercices</Link> — proposer des
            énoncés/variantes d’entraînement.
          </li>
          <li>
            <Link to="/ai/qa">Questions / réponses</Link> — préparer une FAQ ou
            une interrogation orale / écrite.
          </li>
          <li>
            <Link to="/ai/grade-copy">Correction de copie</Link> — analyser un
            scan/PDF d’élève et obtenir une proposition de feedback / note à
            valider.
          </li>
        </ul>
        <h2>Bon réflexe</h2>
        <p>
          Toujours relire avant diffusion : l’IA propose, l’enseignant décide.
          Une fois validé, intégrez le résultat dans une leçon (ressource) ou une
          activité assignée.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'teacher-analytics',
    title: 'Suivre vos classes',
    summary:
      'Différence entre suivi perso et établissement, et ce que vous y lisez pour agir.',
    roles: [TEACHER],
    section: 'modules',
    tags: ['analytics', 'suivi', 'résultats'],
    body: (
      <ArticleBody>
        <h2>À quoi sert le Suivi</h2>
        <p>
          Après avoir publié du contenu et des activités, le module Suivi répond
          à : « Qui a travaillé ? Qui bloque ? Sur quoi ? » Vous n’y créez rien :
          vous observez pour ajuster (relance, remédiation, nouvelle activité).
        </p>
        <h2>Deux vues</h2>
        <ul>
          <li>
            <Link to="/analytics">Activités personnelles</Link> — votre production
            et l’engagement sur <em>vos</em> activités (tentatives, scores,
            tendances).
          </li>
          <li>
            <Link to="/analytics/establishment">Activités établissement</Link> —
            une vue plus large (selon vos droits) pour comparer classes ou
            repérer des signaux faibles au niveau collège / école.
          </li>
        </ul>
        <h2>Ce que vous en faites</h2>
        <ol>
          <li>Repérer les activités peu tentées ou mal réussies.</li>
          <li>Identifier les élèves en difficulté.</li>
          <li>
            Revenir sur le cours (nouvelle leçon / ressource) ou créer une
            activité de remédiation, éventuellement avec l’IA.
          </li>
        </ol>
      </ArticleBody>
    ),
  },
  {
    slug: 'teacher-communication',
    title: 'Annonces et emails',
    summary:
      'Différence annonce vs email, et cas d’usage typiques en classe.',
    roles: [TEACHER],
    section: 'modules',
    tags: ['annonces', 'emails', 'communication'],
    body: (
      <ArticleBody>
        <h2>Rôle du module</h2>
        <p>
          La communication sert à informer sans passer par le contenu de cours :
          dates, consignes, rappels, infos de vie de classe. Elle ne remplace
          pas une leçon ni une évaluation.
        </p>
        <h2>Annonces vs emails</h2>
        <ul>
          <li>
            <Link to="/communication/announcements">Annonces</Link> — messages
            visibles dans Sphere (fil d’établissement / classe selon le ciblage).
            Idéal pour une info durable (« devoir pour vendredi », « sortie »).
          </li>
          <li>
            <Link to="/communication/emails">Emails</Link> — envoi plus direct /
            ciblé quand il faut joindre hors plateforme ou un public précis.
          </li>
        </ul>
        <h2>Ce que vous y faites</h2>
        <ol>
          <li>Rédiger le message et choisir les destinataires.</li>
          <li>Publier (annonce) ou envoyer (email).</li>
          <li>
            Suivre si besoin via le centre de notifications côté destinataires.
          </li>
        </ol>
      </ArticleBody>
    ),
  },

  // ─── Student ───────────────────────────────────────────────
  {
    slug: 'student-getting-started',
    title: 'Premiers pas élève',
    summary:
      'À quoi sert votre espace Sphere et à quoi correspondent les menus.',
    roles: [STUDENT],
    section: 'start',
    tags: ['démarrage', 'vue d’ensemble', 'modules'],
    body: (
      <ArticleBody>
        <h2>Ce que vous faites sur Sphere</h2>
        <p>
          Sphere est votre cahier de cours + vos exercices en ligne. Vous
          consultez ce que le professeur a préparé, vous vous entraînez, vous
          rendez des évaluations, et vous voyez vos résultats.
        </p>
        <h2>Vos modules</h2>
        <ul>
          <li>
            <strong>Mes Cours</strong> — leçons et documents à lire / regarder.
          </li>
          <li>
            <strong>S’entraîner</strong> — exercices et évaluations à faire.
          </li>
          <li>
            <strong>Mes tentatives en cours</strong> — ce que vous avez commencé
            sans finir.
          </li>
          <li>
            <strong>Mes Activités (suivi)</strong> — vos scores et progrès.
          </li>
          <li>
            <strong>IA Studio</strong> — aide à la révision (pas pour créer le
            cours de la classe).
          </li>
        </ul>
        <h2>Premier parcours type</h2>
        <ol>
          <li>
            Ouvrir <Link to="/teachings">Mes Cours</Link> et lire la leçon du
            jour.
          </li>
          <li>
            Aller dans <Link to="/learning/exercises">S’entraîner</Link> pour
            faire l’activité associée.
          </li>
          <li>
            Vérifier le résultat dans <Link to="/analytics">Mes Activités</Link>
            .
          </li>
        </ol>
      </ArticleBody>
    ),
  },
  {
    slug: 'student-activities',
    title: 'Réaliser une activité',
    summary:
      'Entraînement, tentative, révision : ce qui se passe quand vous lancez un exercice.',
    roles: [STUDENT],
    section: 'workflows',
    tags: ['activité', 'tentative', 'révision', 'entraînement'],
    body: (
      <ArticleBody>
        <h2>À quoi ça sert</h2>
        <p>
          Une activité est un travail demandé par l’enseignant (quiz,
          évaluation, exercice). Sphere enregistre votre passage pour que vous
          (et le prof) voyiez le résultat.
        </p>
        <h2>Déroulement typique</h2>
        <ol>
          <li>
            Dans <Link to="/learning/exercises">S’entraîner</Link>, choisissez
            une activité assignée.
          </li>
          <li>
            Répondez aux questions : une <strong>tentative</strong> est créée
            (votre essai en cours).
          </li>
          <li>
            Validez / terminez pour obtenir un score ou un feedback.
          </li>
          <li>
            Si une révision est proposée, refaites les points faibles.
          </li>
        </ol>
        <h2>Tentatives en cours</h2>
        <p>
          Si vous quittez avant la fin, retrouvez l’essai dans{' '}
          <Link to="/learning/in-progress">Mes tentatives en cours</Link> pour
          reprendre sans tout recommencer (selon les règles de l’activité).
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'student-resources',
    title: 'Consulter les ressources',
    summary:
      'Où sont les PDF et supports de cours, et comment les utiliser avant un exercice.',
    roles: [STUDENT],
    section: 'workflows',
    tags: ['ressource', 'leçon', 'cours', 'PDF'],
    body: (
      <ArticleBody>
        <h2>À quoi servent les ressources</h2>
        <p>
          Les ressources sont les supports du cours : PDF, vidéo, lien, image.
          Elles sont attachées aux leçons par vos enseignants. Vous les
          consultez pour comprendre avant (ou pendant) un exercice.
        </p>
        <h2>Ce que vous y faites</h2>
        <ol>
          <li>
            Ouvrez <Link to="/teachings">Mes Cours</Link>.
          </li>
          <li>Choisissez un enseignement puis une leçon.</li>
          <li>
            Ouvrez les ressources listées (téléchargement ou lecture selon le
            type).
          </li>
        </ol>
        <p>
          Vous ne créez pas de ressources : si un document manque, contactez
          l’enseignant.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'student-analytics',
    title: 'Suivre mes progrès',
    summary:
      'Lire Mes Activités : scores, tentatives récentes, et comment s’en servir.',
    roles: [STUDENT],
    section: 'modules',
    tags: ['analytics', 'progrès', 'scores'],
    body: (
      <ArticleBody>
        <h2>Rôle du suivi</h2>
        <p>
          <Link to="/analytics">Mes Activités</Link> n’est pas un cours : c’est
          le tableau de bord de vos résultats. Il montre ce que vous avez déjà
          fait et où vous êtes en difficulté.
        </p>
        <h2>Ce que vous y regardez</h2>
        <ul>
          <li>Tentatives récentes et scores.</li>
          <li>Tendances (amélioration ou baisse sur un type d’activité).</li>
          <li>Activités à reprendre.</li>
        </ul>
        <h2>Ensuite</h2>
        <p>
          Revenez à la leçon concernée dans Mes Cours, ou refaites un
          entraînement. L’IA Studio peut aider à réviser un point précis.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'student-ai-studio',
    title: 'IA Studio côté élève',
    summary:
      'Aide à la révision : ce que vous pouvez faire (et ce qui reste réservé aux profs).',
    roles: [STUDENT],
    section: 'modules',
    tags: ['ia', 'révision', 'aide'],
    body: (
      <ArticleBody>
        <h2>À quoi ça sert pour vous</h2>
        <p>
          <Link to="/ai">IA Studio</Link> est un assistant de révision : quiz
          d’entraînement, synthèse d’un texte, questions/réponses pour vérifier
          que vous avez compris. Ce n’est pas l’espace pour créer le cours de la
          classe ni noter les autres.
        </p>
        <h2>Ce que vous y faites</h2>
        <ul>
          <li>Générer un quiz sur un thème à réviser.</li>
          <li>Demander une synthèse pour retenir l’essentiel.</li>
          <li>S’entraîner en Q&amp;A avant une évaluation.</li>
        </ul>
        <p>
          Les devoirs officiels et notes viennent des activités assignées par
          l’enseignant dans S’entraîner — pas de l’IA Studio.
        </p>
      </ArticleBody>
    ),
  },

  // ─── Principal / Admin ─────────────────────────────────────
  {
    slug: 'leadership-getting-started',
    title: 'Premiers pas direction / admin',
    summary:
      'Vue d’ensemble des modules de pilotage et par où commencer.',
    roles: [...LEADERSHIP],
    section: 'start',
    tags: ['démarrage', 'établissement', 'modules'],
    body: (
      <ArticleBody>
        <h2>Votre rôle sur Sphere</h2>
        <p>
          Vous ne construisez pas le détail de chaque leçon au quotidien : vous
          organisez l’établissement, les personnes, les classes, la
          communication, et vous surveillez les indicateurs pédagogiques.
        </p>
        <h2>Modules principaux</h2>
        <ul>
          <li>
            <strong>Mon Établissement</strong> — fiche et identité de
            l’établissement.
          </li>
          <li>
            <strong>Accès &amp; Membres</strong> — qui a quel rôle.
          </li>
          <li>
            <strong>Classes &amp; Groupes</strong> — organisation des effectifs.
          </li>
          <li>
            <strong>Enseignement / Contenus</strong> — supervision de ce qui est
            en place (lecture / pilotage).
          </li>
          <li>
            <strong>Communication</strong> — annonces et emails établissement.
          </li>
          <li>
            <strong>Analytics établissement</strong> — suivi global et alertes.
          </li>
        </ul>
        <h2>Ordre recommandé le premier jour</h2>
        <ol>
          <li>
            <Link to="/establishment">Mon Établissement</Link> — vérifier les
            infos.
          </li>
          <li>
            <Link to="/members">Membres</Link> — contrôler les rôles.
          </li>
          <li>
            <Link to="/classes">Classes</Link> — valider la structure.
          </li>
          <li>
            Puis communication et analytics selon le besoin.
          </li>
        </ol>
      </ArticleBody>
    ),
  },
  {
    slug: 'leadership-members-classes',
    title: 'Membres et classes',
    summary:
      'Gérer l’annuaire et les effectifs : à quoi ça sert et actions typiques.',
    roles: [...LEADERSHIP],
    section: 'workflows',
    tags: ['membres', 'classes', 'inscriptions', 'rôles'],
    body: (
      <ArticleBody>
        <h2>Membres — à quoi ça sert</h2>
        <p>
          <Link to="/members">Accès &amp; Membres</Link> est l’annuaire : chaque
          personne a un rôle (enseignant, élève, direction…). Sans bon
          rattachement, elle ne voit pas les bons menus ni les bonnes classes.
        </p>
        <h2>Ce que vous y faites</h2>
        <ul>
          <li>Consulter / filtrer par rôle.</li>
          <li>Ajouter ou mettre à jour un membre.</li>
          <li>Corriger un mauvais rôle (ex. élève vu comme enseignant).</li>
        </ul>
        <h2>Classes — à quoi ça sert</h2>
        <p>
          <Link to="/classes">Classes &amp; Groupes</Link> regroupe les élèves
          (et parfois enseignants) pour l’emploi du temps pédagogique. Les
          enseignements et assignations d’activités s’appuient sur ces groupes.
        </p>
        <h2>Ce que vous y faites</h2>
        <ol>
          <li>Créer une classe et la lier à un niveau.</li>
          <li>Y ajouter les élèves (effectifs).</li>
          <li>
            Vérifier qu’un enseignement (matière) pourra ensuite être rattaché.
          </li>
        </ol>
      </ArticleBody>
    ),
  },
  {
    slug: 'leadership-supervision',
    title: 'Superviser le pédagogique',
    summary:
      'Lire enseignements et contenus pour piloter, sans tout créer soi-même.',
    roles: [...LEADERSHIP],
    section: 'workflows',
    tags: ['supervision', 'enseignements', 'contenu'],
    body: (
      <ArticleBody>
        <h2>Objectif</h2>
        <p>
          Vérifier que les cours sont en place (enseignements, chapitres,
          leçons) et que les classes ne sont pas « vides » pédagogiquement. La
          création fine reste surtout côté enseignants ; vous contrôlez la
          couverture et les manques.
        </p>
        <h2>Ce que vous y faites</h2>
        <ol>
          <li>
            Parcourir <Link to="/teachings">les enseignements</Link>.
          </li>
          <li>
            Ouvrir chapitres / leçons / ressources pour un contrôle ponctuel.
          </li>
          <li>
            Demander aux enseignants de compléter ce qui manque, ou utiliser
            Communication pour une consigne d’établissement.
          </li>
        </ol>
      </ArticleBody>
    ),
  },
  {
    slug: 'leadership-analytics',
    title: 'Analytics établissement',
    summary:
      'Lire le tableau de bord global : signaux, classes à risque, actions possibles.',
    roles: [...LEADERSHIP],
    section: 'modules',
    tags: ['analytics', 'risque', 'établissement', 'pilotage'],
    body: (
      <ArticleBody>
        <h2>À quoi sert cette vue</h2>
        <p>
          <Link to="/analytics/establishment">Activités établissement</Link>{' '}
          agrège l’activité pédagogique : qui travaille, où ça coince, quelles
          classes ou élèves sortent du lot (y compris signaux « à risque »).
        </p>
        <h2>Ce que vous y faites</h2>
        <ul>
          <li>Comparer l’engagement entre classes ou matières.</li>
          <li>Repérer les élèves / groupes en difficulté.</li>
          <li>
            Décider d’une action : échange avec l’enseignant, remédiation,
            communication aux familles selon votre process.
          </li>
        </ul>
        <p>
          Ce module ne crée pas d’activités : il éclaire les décisions de
          pilotage.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'leadership-communication',
    title: 'Communication établissement',
    summary:
      'Annonces et emails au niveau établissement : usages et différences.',
    roles: [...LEADERSHIP],
    section: 'modules',
    tags: ['annonces', 'emails', 'communication'],
    body: (
      <ArticleBody>
        <h2>Rôle</h2>
        <p>
          Informer toute la communauté (ou un sous-ensemble) : rentrée,
          organisation, consignes transverses. Distinct du contenu de cours.
        </p>
        <ul>
          <li>
            <Link to="/communication/announcements">Annonces</Link> — visibles
            dans Sphere, idéales pour une info institutionnelle.
          </li>
          <li>
            <Link to="/communication/emails">Emails</Link> — diffusion plus
            large ou hors connexion plateforme.
          </li>
        </ul>
        <h2>Actions typiques</h2>
        <ol>
          <li>Rédiger et cibler (établissement, rôles, classes…).</li>
          <li>Publier / envoyer.</li>
          <li>Suivre les retours via les canaux habituels.</li>
        </ol>
      </ArticleBody>
    ),
  },

  // ─── Administrative manager ────────────────────────────────
  {
    slug: 'admin-mgr-getting-started',
    title: 'Premiers pas gestionnaire',
    summary:
      'Votre périmètre : établissement, personnes, classes — sans contenu pédagogique.',
    roles: [ADMINISTRATIVE_MANAGER],
    section: 'start',
    tags: ['démarrage', 'établissement', 'modules'],
    body: (
      <ArticleBody>
        <h2>Ce que vous faites sur Sphere</h2>
        <p>
          Vous gardez l’annuaire et l’organisation des groupes à jour. Vous ne
          créez pas de leçons, d’évaluations ni de contenus IA : dès qu’il
          s’agit de pédagogie, vous orientez vers un enseignant ou la direction.
        </p>
        <h2>Vos modules</h2>
        <ul>
          <li>
            <Link to="/establishment">Mon Établissement</Link> — infos de base.
          </li>
          <li>
            <Link to="/members">Accès &amp; Membres</Link> — inscriptions et
            rôles.
          </li>
          <li>
            <Link to="/classes">Classes &amp; Groupes</Link> — effectifs.
          </li>
        </ul>
      </ArticleBody>
    ),
  },
  {
    slug: 'admin-mgr-members-classes',
    title: 'Inscriptions et effectifs',
    summary:
      'Actions concrètes sur les membres et les classes au quotidien.',
    roles: [ADMINISTRATIVE_MANAGER],
    section: 'workflows',
    tags: ['membres', 'classes', 'inscriptions'],
    body: (
      <ArticleBody>
        <h2>Membres</h2>
        <p>
          Créer ou mettre à jour une personne, lui attribuer le bon rôle, vérifier
          qu’elle est rattachée au bon établissement. Un mauvais rôle = mauvais
          menu à la connexion.
        </p>
        <h2>Classes</h2>
        <p>
          Créer la classe, rattacher le niveau, y placer les élèves. C’est la
          base pour que les enseignants puissent ensuite ouvrir un enseignement
          sur ce groupe.
        </p>
        <h2>Limite de votre rôle</h2>
        <p>
          Pas de guides « créer une leçon » ou « générer un quiz » ici : hors
          périmètre gestionnaire.
        </p>
      </ArticleBody>
    ),
  },

  // ─── Pedagogical coordinator ───────────────────────────────
  {
    slug: 'coord-getting-started',
    title: 'Premiers pas coordinateur',
    summary:
      'Votre mission de cohérence pédagogique et les modules associés.',
    roles: [PEDAGOGICAL_COORDINATOR],
    section: 'start',
    tags: ['démarrage', 'coordination', 'modules'],
    body: (
      <ArticleBody>
        <h2>Votre rôle</h2>
        <p>
          Vous accompagnez la qualité et la cohérence des enseignements : classes,
          cours, et appui via l’IA. Vous n’êtes pas le cockpit admin complet
          (membres / établissement au sens gestionnaire).
        </p>
        <h2>Modules</h2>
        <ul>
          <li>
            <Link to="/classes">Mes Classes</Link> — groupes concernés.
          </li>
          <li>
            <Link to="/teachings">Mes Cours</Link> — enseignements à superviser.
          </li>
          <li>
            <Link to="/ai">IA Studio</Link> — prototypage / aide à la
            production pour validation.
          </li>
        </ul>
      </ArticleBody>
    ),
  },
  {
    slug: 'coord-teaching',
    title: 'Superviser les enseignements',
    summary:
      'Contrôler la structure des cours et ce que vous demandez ensuite aux enseignants.',
    roles: [PEDAGOGICAL_COORDINATOR],
    section: 'workflows',
    tags: ['enseignements', 'supervision', 'contenu'],
    body: (
      <ArticleBody>
        <h2>Objectif</h2>
        <p>
          S’assurer qu’un enseignement a bien des chapitres et leçons, que le
          progressions tient la route, et signaler les trous.
        </p>
        <h2>Ce que vous y faites</h2>
        <ol>
          <li>
            Parcourir <Link to="/teachings">Mes Cours</Link>.
          </li>
          <li>Vérifier la présence de séquences (chapitres / leçons).</li>
          <li>
            Faire remonter aux enseignants ce qui manque ; la création fine leur
            revient en général.
          </li>
        </ol>
      </ArticleBody>
    ),
  },
  {
    slug: 'coord-ai',
    title: 'IA Studio (coordinateur)',
    summary:
      'Prototyper et valider des contenus avant transmission aux enseignants.',
    roles: [PEDAGOGICAL_COORDINATOR],
    section: 'modules',
    tags: ['ia', 'validation'],
    body: (
      <ArticleBody>
        <h2>Usage typique</h2>
        <p>
          Dans <Link to="/ai">IA Studio</Link>, vous pouvez prototyper un quiz
          ou une synthèse pour un niveau / une matière, contrôler la qualité, puis
          transmettre à l’enseignant pour publication dans le cours ou les
          activités.
        </p>
        <p>
          L’IA ne remplace pas la validation pédagogique : elle accélère
          l’exploration de contenus.
        </p>
      </ArticleBody>
    ),
  },

  // ─── Support ───────────────────────────────────────────────
  {
    slug: 'support-getting-started',
    title: 'Premiers pas support',
    summary:
      'Diagnostic lecture seule : ce que vous voyez et ce que vous ne faites pas.',
    roles: [SUPPORT],
    section: 'start',
    tags: ['démarrage', 'diagnostic'],
    body: (
      <ArticleBody>
        <h2>Votre rôle</h2>
        <p>
          Aider les utilisateurs bloqués en vérifiant le contexte
          (établissement, membre, rôle) sans créer de contenu pédagogique ni
          publier d’annonces à leur place.
        </p>
        <h2>Modules utiles</h2>
        <ul>
          <li>
            <Link to="/establishment">Mon Établissement</Link> — contexte.
          </li>
          <li>
            <Link to="/members">Accès &amp; Membres</Link> — vérifier rôle et
            rattachement.
          </li>
        </ul>
        <p>
          Pas de guides « créer / publier » : si le besoin est pédagogique,
          orientez vers le bon profil (voir guide d’orientation).
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'support-orientation',
    title: 'Orienter les utilisateurs',
    summary:
      'Quel module et quel guide indiquer selon le rôle de la personne aidée.',
    roles: [SUPPORT],
    section: 'workflows',
    tags: ['orientation', 'rôles', 'modules'],
    body: (
      <ArticleBody>
        <h2>Principe</h2>
        <p>
          Chaque rôle a un hub <Link to="/docs">/docs</Link> différent. Demandez
          à la personne de se connecter avec son compte : elle verra uniquement
          ses guides.
        </p>
        <h2>Orientation rapide</h2>
        <ul>
          <li>
            <strong>Enseignant</strong> — cours, contenus, activités, IA,
            suivi.
          </li>
          <li>
            <strong>Élève</strong> — Mes Cours, S’entraîner, tentatives, Mes
            Activités.
          </li>
          <li>
            <strong>Direction / admin</strong> — établissement, membres,
            classes, analytics.
          </li>
          <li>
            <strong>Gestionnaire</strong> — membres et classes uniquement.
          </li>
          <li>
            <strong>Coordinateur</strong> — enseignements + IA.
          </li>
        </ul>
        <h2>Check diagnostic</h2>
        <ol>
          <li>Bon établissement sélectionné ?</li>
          <li>Bon rôle sur la fiche membre ?</li>
          <li>Rattaché à la bonne classe ?</li>
        </ol>
      </ArticleBody>
    ),
  },

  // ─── Glossary (role-filtered) ──────────────────────────────
  {
    slug: 'glossary-teaching-unit',
    title: 'Enseignement, chapitre, leçon',
    summary:
      'Définitions et rôle de chaque brique dans le parcours pédagogique.',
    roles: [TEACHER, PRINCIPAL, ADMIN, PEDAGOGICAL_COORDINATOR, SUPPORT],
    section: 'glossary',
    tags: ['glossaire', 'enseignement', 'chapitre', 'leçon'],
    body: (
      <ArticleBody>
        <ul>
          <li>
            <strong>Enseignement</strong> — qui enseigne quoi à qui (classe +
            matière + niveau). Cadre organisationnel, pas encore le contenu.
          </li>
          <li>
            <strong>Chapitre</strong> — séquence thématique dans un
            enseignement (découpage du programme).
          </li>
          <li>
            <strong>Leçon</strong> — unité d’apprentissage consultable par
            l’élève ; y sont accrochées les ressources.
          </li>
        </ul>
        <p>
          Ordre logique : enseignement → chapitres → leçons → ressources, puis
          activités pour faire travailler.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'glossary-resource',
    title: 'Ressource',
    summary: 'Support de cours (fichier ou lien) attaché à une leçon.',
    roles: [
      TEACHER,
      STUDENT,
      PRINCIPAL,
      ADMIN,
      PEDAGOGICAL_COORDINATOR,
      SUPPORT,
    ],
    section: 'glossary',
    tags: ['glossaire', 'ressource'],
    body: (
      <ArticleBody>
        <p>
          Une <strong>ressource</strong> est un support pédagogique : PDF,
          image, vidéo, lien web. L’enseignant la publie sur une leçon ;
          l’élève la consulte pour apprendre. Ce n’est pas une évaluation (pas
          de score) — pour ça, voir activité / tentative.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'glossary-activity-attempt',
    title: 'Activité et tentative',
    summary: 'Travail à faire vs passage concret d’un élève.',
    roles: [
      TEACHER,
      STUDENT,
      PRINCIPAL,
      ADMIN,
      PEDAGOGICAL_COORDINATOR,
      SUPPORT,
    ],
    section: 'glossary',
    tags: ['glossaire', 'activité', 'tentative'],
    body: (
      <ArticleBody>
        <ul>
          <li>
            <strong>Activité</strong> — le travail proposé (exercice,
            évaluation) : énoncé, type, éventuellement barème.
          </li>
          <li>
            <strong>Tentative</strong> — l’essai d’un élève sur cette activité
            (en cours, terminé, score).
          </li>
        </ul>
        <p>
          L’enseignant crée et assigne l’activité ; l’élève produit des
          tentatives ; le suivi affiche les résultats.
        </p>
      </ArticleBody>
    ),
  },
  {
    slug: 'glossary-establishment',
    title: 'Établissement et membres',
    summary: 'Contexte organisationnel : école et personnes rattachées.',
    roles: [PRINCIPAL, ADMIN, ADMINISTRATIVE_MANAGER, SUPPORT, TEACHER],
    section: 'glossary',
    tags: ['glossaire', 'établissement', 'membres'],
    body: (
      <ArticleBody>
        <ul>
          <li>
            <strong>Établissement</strong> — l’école / collège dans lequel vous
            travaillez (sélectionnable dans la barre du haut).
          </li>
          <li>
            <strong>Membre</strong> — personne rattachée avec un rôle
            (enseignant, élève, direction…). Le rôle détermine les menus et la
            documentation visibles.
          </li>
        </ul>
      </ArticleBody>
    ),
  },
];
